import type { MergeHandlerMap } from '../configMerge';
import deepmerge from 'deepmerge';
import type { SchemaOf } from '../../helpers';
import { Joi, objectSchemaOf, makeAllRequired } from '../../helpers';

export interface ITouchFallbackOption {
  intent: string;
  text: string;
}
export const touchFallbackOptionSchema = objectSchemaOf<ITouchFallbackOption>({
  intent: Joi.string().required(),
  text: Joi.string().required(),
});

export interface IShortcutMessage {
  displayName: string;
  values: string[];
}
export const shortcutMessageSchema = objectSchemaOf<IShortcutMessage>({
  displayName: Joi.string().required(),
  values: Joi.array().items(Joi.string()).min(1).required(),
});

export interface IConfig {
  botIntroMessage: string;
  botTouchFallbackMessage: string;
  botTouchFallbackOptions: ITouchFallbackOption[];
  botNoReplyMessage: string;
  onInterventionMessages: string[];
  dialogueTimeout: number;
  vocalisationTimeout: number;
  noReplyFromHilTimeout: number;
  noReplyFromBotTimeout: number;
  noReplyFromUserTimeout: number;
  noInterventionTimeout: number;
  serverMessageTimeout: number;
  ringBellMessages: string[];
  delayAfterEndConversationMessage: number;
  orderedShortcutMessages: {
    [key: number]: IShortcutMessage[]; // this is a restriction of firestore not allowing nested arrays
  };
  noUserInputKeyTimeout: number;
  userCues: { [key: string]: string | null }; // set value to null to make the prompt not clickable.
  invitationConfirmAttendeeNameMessage: string;
}

const customMergeMap: Partial<MergeHandlerMap<IConfig>> = {
  userCues: (_, source) => source,
  orderedShortcutMessages: (_, source) => source,
  botTouchFallbackOptions: (_, source) => source,
};

export const mergeConfig = (
  target?: Partial<IConfig>,
  source?: Partial<IConfig>
): Partial<IConfig> => {
  if (!target && !source) {
    return {};
  }

  return deepmerge.all<Partial<IConfig>>([{}, target || {}, source || {}], {
    customMerge: (key: keyof IConfig) => customMergeMap[key],
  });
};

export const schemaMap: SchemaOf<IConfig> = {
  botIntroMessage: Joi.string(),
  botTouchFallbackMessage: Joi.string(),
  botTouchFallbackOptions: Joi.array().items(touchFallbackOptionSchema).min(1),
  botNoReplyMessage: Joi.string(),
  onInterventionMessages: Joi.array().items(Joi.string().required()),
  dialogueTimeout: Joi.number(),
  vocalisationTimeout: Joi.number(),
  noReplyFromHilTimeout: Joi.number(),
  noReplyFromBotTimeout: Joi.number(),
  noReplyFromUserTimeout: Joi.number(),
  noInterventionTimeout: Joi.number(),
  serverMessageTimeout: Joi.number(),
  ringBellMessages: Joi.array().items(Joi.string().required()),
  delayAfterEndConversationMessage: Joi.number(),
  orderedShortcutMessages: Joi.object().pattern(
    Joi.number(),
    Joi.array().items(shortcutMessageSchema)
  ),
  noUserInputKeyTimeout: Joi.number(),
  userCues: Joi.object().pattern(
    Joi.string().required(),
    Joi.string().allow(null).required()
  ),
  invitationConfirmAttendeeNameMessage: Joi.string(),
};

export const schemaMapDefault = makeAllRequired(schemaMap);

export const defaultConfig: IConfig = {
  botIntroMessage: `Hi. I'm Aimee. How can I help?`,
  botTouchFallbackMessage: `What can I help you with?`,
  botTouchFallbackOptions: [
    { text: 'Meeting', intent: 'meeting' },
    { text: 'Delivery', intent: 'delivery' },
    { text: 'Other', intent: 'other' },
  ],
  botNoReplyMessage: 'Are you still there?',
  dialogueTimeout: 300000,
  vocalisationTimeout: 10000,
  noReplyFromHilTimeout: 10000,
  noReplyFromBotTimeout: 6200,
  noReplyFromUserTimeout: 20000,
  noInterventionTimeout: 10000,
  serverMessageTimeout: 1000,
  onInterventionMessages: [
    'Just one moment please.',
    'Just a second.',
    'Give me a moment.',
  ],
  ringBellMessages: [
    "I'm just contacting someone in the office now. Won't be long.",
    "I've just let someone in the office know. They won't be long.",
  ],
  delayAfterEndConversationMessage: 3000,
  orderedShortcutMessages: {
    0: [
      {
        displayName: 'Your name?',
        values: [
          'Can I have your name please?',
          'Can I please have your name?',
          'And your name is?',
        ],
      },
      {
        displayName: 'Staff name?',
        values: [
          'Who are you here to meet?',
          'Can I have the name of the person you are visiting?',
          'Great. And who are you here to visit?',
          'Got that. And who are you seeing today?',
        ],
      },
      {
        displayName: 'Courier',
        values: [
          'Are you here to pick up or deliver?',
          'Do you need a signature for that?',
          'I am calling someone to come out shortly.',
        ],
      },
      {
        displayName: 'Expecting you?',
        values: [
          'Is anybody expecting you?',
          'Is someone expecting you today?',
        ],
      },
    ],
    1: [
      {
        displayName: 'Almost there',
        values: [
          'Almost there.',
          'Sorry to keep you waiting.',
          "Oops. Let's start again.",
        ],
      },
      {
        displayName: 'Thanks.',
        values: ['Thanks.', 'Nice to meet you.', 'Have a good day.'],
      },
      {
        displayName: 'Take a seat',
        values: ['Please take a seat.', "Won't be long."],
      },
    ],
    2: [
      {
        displayName: 'Contacted staff',
        values: [
          "I'm checking the directory for {{staffFirstName}}.",
          "Messaging {{staffFirstName}} for you. Won't be long.",
          'Ok. Just contacting {{staffFirstName}}.',
          'I am sending a message to {{staffFirstName}} right now.',
        ],
      },
      {
        displayName: 'Staff not in',
        values: [
          "I can't find {{staffFirstName}} in the directory. Let me call someone in the office for you.",
          "I'm afraid {{staffFirstName}} is not in today. Do you want to see someone else?",
        ],
      },
      {
        displayName: 'Someone coming',
        values: [
          "I have contacted someone to come out. Won't be long.",
          'Ok, calling someone to jump out of the office for you.',
          "No worries. I've called someone to come over.",
        ],
      },
    ],
  },
  noUserInputKeyTimeout: 10000,
  userCues: {
    Meeting: 'meeting',
    Delivery: 'delivery',
  },
  invitationConfirmAttendeeNameMessage: 'Are you {{visitorName}}?',
};
